import {
  ClientStatus,
  User,
} from "api/types";
import {
  archiveAccount,
  useGetAdminStaffs,
  useGetClientDetail,
} from "api/user";
import Badge from "components/Badge";
import Button from "components/Button";
import { Icon } from "components/Icon";
import Modal from "components/Modal";
import { Option } from "components/Select";
import Tabs from "components/Tabs";
import { FormData } from "components/types";
import { invalidateQueries } from "components/utility";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useLoaderData } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import { getProfileStatusBadgeProps } from "components/ClientDetail";
import {
  capitalizeFirstLetter,
  getFormattedDate,
} from "utils/util";
import ApplicationMenu from "../dashboard/Menu";
import { useClientProfileOverviewContent } from "./viewClientDetailUtility";
import { StepNames, useEditableForm } from "./clientDetail";

export default function ViewClientDetail() {
  const {
    user: masterUserData,
    admin,
    isSuperAdmin,
  } = useLoaderData() as {
    user: User;
    admin: User;
    isSuperAdmin: boolean;
  };

  const { data: user } = useGetClientDetail(masterUserData.id);
  const formMethods = useForm<FormData>();
  const formMethods1 = useForm<FormData>();
  const [isOpen, setIsOpen] = useState(false);
  const [showArchiveStaff, setShowArchiveStaff] = useState(false);
  const [stepName, setStepName] = useState<StepNames>();
  const [adminStaffOptions, setAdminStaffOptions] = useState<Option[]>([]);
  const { data, isFetching } = useGetAdminStaffs();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (data) {
      const adminStaffOptions = data.map((admin) => ({
        label: admin.username,
        value: admin.id,
      }));
      setAdminStaffOptions(adminStaffOptions);
    }
  }, [data, isFetching]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const handleCloseModal = () => {
    setIsOpen(false);
    setStepName(undefined);
    formMethods.reset();
    invalidateQueries(queryClient, [
      `/api/user/client/profile/${masterUserData.id}`,
      `/api/user/admin/client/review-logs/${masterUserData.id}`,
      `/api/user/admin/client/user-data-logs/${masterUserData.id}`,
      `/api/user/admin/client`,
    ]);
  };

  const onEditClick = (stepName: StepNames) => {
    setStepName(stepName);
    setIsOpen(true);
  };
  
  const archiveStaff = async () => {
    if (!user) {
      return;
    }
    const response = await archiveAccount(user.id);
    if (response.statusText && response.status !== 200) {
      toast.error("Error in archiving staff", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
        icon: <Icon name="error" />,
      });
      return;
    }
    toast.success("Archived staff successfully", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      icon: <Icon name="check" />,
    });
    handleCloseModal();
  };

  const tabs = [
    {
      label: "Profile Overview",
      content: useClientProfileOverviewContent(
        user ? { ...user } : { ...masterUserData },
        onEditClick,
        adminStaffOptions,
        admin,
        isSuperAdmin,
        handleCloseModal,
      ),
    },
    {
      label: "Assesment & Care Plan",
      content: (<h6 className="mt-10">Assesment & Care Plan</h6>),
    },
    {
      label: "Progress Notes",
      content: (<h6 className="mt-10">Progress Notes</h6>),
    },
    {
      label: "Emergency & Medical",
      content: (<h6 className="mt-10">Emergency & Medical</h6>),
    },
    {
      label: "Bank Account & ITF",
      content: (<h6 className="mt-10">Bank Account & ITF</h6>),
    },
    {
      label: "Associated Providers",
      content: (<h6 className="mt-10">Associated Providers</h6>),
    },
    {
      label: "Account History",
      content: (<h6 className="mt-10">Account History</h6>),
    }
  ];

  const clientStatus = user?.clientProfile ? user.clientProfile.profileStatus : ClientStatus.Onboarding;

  return (
    <div>
      <ApplicationMenu />
      <header className="mx-auto max-w-7xl ">
        <div className="font-bold leading-tight tracking-tight text-gray-900 border-b border-gray-300 py-5 flex justify-between">
          <div className="flex space-x-2 items-center">
            <h1 className="text-3xl flex-1">
              {masterUserData.firstName} {masterUserData.lastName || "User"}'s
              Profile
            </h1>
            {user && <Badge {...getProfileStatusBadgeProps(clientStatus)} />}
          </div>
          <div className="flex flex-1 justify-end space-x-3">
            {clientStatus === ClientStatus.Onboarding && (
              <div className="flex items-center space-x-4">
                <Button
                  onClick={() => setShowArchiveStaff(true)}
                  variant="primary"
                >
                  Archive
                </Button>
              </div>
            )}
            <div className="flex flex-col items-start">
              <span className="text-xs leading-4 font-medium tracking-wider uppercase text-gray-700">
                Account created date
              </span>
              <span className="text-sm leading-5 font-medium text-gray-900">
                {getFormattedDate(new Date(masterUserData.createdAt))}
              </span>
            </div>
            <div className="flex flex-col items-start">
              <span className="text-xs leading-4 font-medium tracking-wider uppercase text-gray-700">
                Account created by
              </span>
              <span className="text-sm leading-5 font-medium text-gray-900">
                {capitalizeFirstLetter(masterUserData.createdBy?.username)}
              </span>
            </div>
          </div>
        </div>
      </header>
      <FormProvider {...formMethods1}>
        <div className="mx-auto max-w-7xl  mt-4">
          <Tabs tabs={tabs} syncWithUrl />
        </div>
      </FormProvider>
      <FormProvider {...formMethods}>
        <Modal
          isOpen={isOpen}
          onClose={handleCloseModal}
          modalHeader={`Edit ${(stepName || "")?.split("_")?.join(" ").toLowerCase()}`}
          className="large"
          modalBodyClassName="!m-0"
          modalHeaderClassName="!px-6"
        >
          {useEditableForm(
            user || masterUserData,
            stepName,
            formMethods,
            adminStaffOptions,
            handleCloseModal,
            isSuperAdmin
          )}
          <div className="bg-gray-50 flex justify-end space-x-2 items-center  rounded-b-md px-5 py-3 border-t border-gray-200 w-full mt-3 flex-wrap md:flex-nowrap">
            <Button variant="white" onClick={() => handleCloseModal()}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form={`edit-${stepName}`}>
              Save
            </Button>
          </div>
        </Modal>
        <Modal
          isOpen={showArchiveStaff}
          onClose={() => setShowArchiveStaff(false)}
          modalHeader=""
          className="!bg-primary-50"
          modalBodyClassName="!mt-2"
        >
          <h3 className="text-primary-900 font-bold flex justify-start items-center px-4">
            <Icon name="info" />
            <span className="ml-3">Confirm archive staff</span>
          </h3>
          <p className="text-primary-800 text-sm leading-5 font-normal px-4 pt-4">
            {`Please confirm you are about to archive  ${user?.firstName} ${user?.lastName}'s account `}
          </p>
          <div className="flex justify-start items-center  rounded-b-md px-4 py-3  rounded-b-md space-x-2">
            <Button
              variant="text"
              size="noSize"
              onClick={() => setShowArchiveStaff(false)}
              className="text-primary-800"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="text-primary-800"
              onClick={() => {
                archiveStaff();
                setShowArchiveStaff(false);
              }}
            >
              Confirm
            </Button>
          </div>
        </Modal>
      </FormProvider>
    </div>
  );
}
